import React from 'react';
import { TableCell } from '../TableCell';
import { Icons, SVG, SVGIcon } from '../../../atoms/SVG';
import { useHtmlValidId } from '../../../../hooks/useHtmlValidId';
import { useSonTranslation } from '../../../../translation/useSonTranslation';

export type TableHeaderSortButton = {
  onClick: () => void;
  label: string;
  customTableHeaderClass?: string;
} & TableCell;

/**
 * Button used for Table Headers to sort a column.
 *
 * The sort button has a visual indicator to explain the sort function here (arrows).
 * For screen reader users it has an additional description.
 * It is hidden, to not make it part of the accessible name and not make it selectable or printable.
 * Why? Because the accessible name will also become the name of the table head.
 * The redundant info "sort" would be read on every cell of the corresponding column then.
 */
export function TableHeaderSortButton({
  onClick,
  label,
  customTableHeaderClass,
  ...tableCellProps
}: TableHeaderSortButton) {
  const { t } = useSonTranslation('Table');
  const id = useHtmlValidId();

  const sortDescriptionId = `table-sort-description-${id}`;

  const getSortArrow = (): SVGIcon => {
    switch (tableCellProps.ariaSort) {
      case 'descending':
        return Icons.SORT_ARROW_DOWN;
      case 'ascending':
        return Icons.SORT_ARROW_UP;
      default:
        return Icons.SORT_ARROW_BOTH;
    }
  };

  return (
    <TableCell {...tableCellProps} customTableHeaderClass={customTableHeaderClass}>
      <button type="button" onClick={onClick} className="tables__sort-button" aria-describedby={sortDescriptionId}>
        <span className="tables__sort-button-name">{label}</span>
        <span className="tables__sort-button-icon" aria-hidden="true">
          <SVG svgSpriteId={getSortArrow()} />
        </span>
      </button>
      <span hidden id={sortDescriptionId}>
        {t('sort.description')}
      </span>
    </TableCell>
  );
}
